import type { GeneralLedgerResponse } from '@hesabat/contracts';
import { SOURCE_LABELS } from './AccountLedger';
import type { CsvCell } from './csv';
import { formatMoney, isZero } from './money';

/** صفوف التصدير: لكل حساب ترويسة وافتتاحى وسطوره وختامى — كما يطبعه المحاسب. */
export function generalLedgerRows(gl: GeneralLedgerResponse): CsvCell[][] {
  const rows: CsvCell[][] = [];
  for (const item of gl.accounts) {
    rows.push([item.account.code + ' — ' + item.account.nameAr, '', '', '', '', '', '', '']);
    rows.push([gl.from, '', '', '', 'رصيد افتتاحى', '', '', item.openingBalance]);
    for (const line of item.lines) {
      rows.push([
        line.postingDate,
        line.journalNumber ?? line.journalId,
        SOURCE_LABELS[line.sourceType] ?? line.sourceType,
        line.reference,
        line.memo,
        line.debit,
        line.credit,
        line.runningBalance,
      ]);
    }
    rows.push([
      gl.to,
      '',
      '',
      '',
      'الإجمالى والرصيد الختامى',
      item.totalDebit,
      item.totalCredit,
      item.closingBalance,
    ]);
  }
  rows.push(['', '', '', '', 'إجمالى حركة الفترة', gl.totalDebit, gl.totalCredit, '']);
  return rows;
}

export const GENERAL_LEDGER_HEADER = [
  'التاريخ',
  'القيد',
  'النوع',
  'المرجع',
  'البيان',
  'مدين',
  'دائن',
  'الرصيد',
];

/**
 * الأستاذ العام: كشف كل حساب تحت الآخر. الأرقام من الـ API كما هى، وكل كشف
 * يُبنى فى الخادم بنفس دالة شاشة كشف الحساب — فلا يختلفان.
 */
export default function GeneralLedger({
  gl,
  onOpenLedger,
}: {
  gl: GeneralLedgerResponse;
  onOpenLedger: (code: string) => void;
}) {
  if (gl.accounts.length === 0) {
    return <p className="sub">لا أرصدة ولا حركة فى هذه الفترة.</p>;
  }
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            {GENERAL_LEDGER_HEADER.map((title) => (
              <th key={title}>{title}</th>
            ))}
          </tr>
        </thead>
        {gl.accounts.map((item) => (
          <tbody key={item.account.code}>
            <tr
              className="section-head clickable"
              tabIndex={0}
              onClick={() => onOpenLedger(item.account.code)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onOpenLedger(item.account.code);
              }}
            >
              <td colSpan={8}>
                {item.account.code} — {item.account.nameAr}
              </td>
            </tr>
            <tr className="opening">
              <td>{gl.from}</td>
              <td colSpan={6}>رصيد افتتاحى</td>
              <td className="num-cell">{formatMoney(item.openingBalance)}</td>
            </tr>
            {item.lines.map((line) => (
              <tr key={line.journalId + ':' + line.lineNumber}>
                <td>{line.postingDate}</td>
                <td className="mono" title={line.journalId}>
                  {line.journalNumber ?? line.journalId.slice(0, 8)}
                </td>
                <td>{SOURCE_LABELS[line.sourceType] ?? line.sourceType}</td>
                <td>{line.reference ?? ''}</td>
                <td>{line.memo ?? ''}</td>
                <td className="num-cell">{isZero(line.debit) ? '' : formatMoney(line.debit)}</td>
                <td className="num-cell">{isZero(line.credit) ? '' : formatMoney(line.credit)}</td>
                <td className="num-cell">{formatMoney(line.runningBalance)}</td>
              </tr>
            ))}
            <tr className="subtotal">
              <td>{gl.to}</td>
              <td colSpan={4}>الإجمالى والرصيد الختامى</td>
              <td className="num-cell">{formatMoney(item.totalDebit)}</td>
              <td className="num-cell">{formatMoney(item.totalCredit)}</td>
              <td className="num-cell">{formatMoney(item.closingBalance)}</td>
            </tr>
          </tbody>
        ))}
        <tfoot>
          <tr className="closing">
            <td colSpan={5}>إجمالى حركة الفترة على كل الحسابات</td>
            <td className="num-cell total">{formatMoney(gl.totalDebit)}</td>
            <td className="num-cell total">{formatMoney(gl.totalCredit)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
